import { ChangeDetectorRef, Component, DestroyRef, Injector, computed, inject } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormArray, FormGroup, FormsModule } from '@angular/forms';
import {
  KebabMenuItem,
  QaiBreadcrumbListComponent,
  QaiButtonComponent,
  QaiFloatingDirective,
  QaiKebabMenuComponent,
  QaiSimulationCardComponent,
  QaiTabDirective,
  QaiTabsComponent,
  QaiToastService,
  QaiTooltipComponent,
} from '@qai/ui';
import { GeneralContentComponent } from './general-details/general-content.component';
import { TestingDetailsComponent } from './testing-details/testing-details.component';
import { ContentDetailComponent } from './content-details/content-detail.component';
import { ScoringDetailsComponent } from './scoring-details/scoring-details.component';
import { KeyWordsDetailsComponent } from './key-words-details/key-words-details.component';
import { WhatUsersSeeDetailsComponent } from './what-users-see-details/what-users-see-details.component';
import { PublishSimulationModalComponent } from '../components/modals/publish-simulation/publish-simulation-modal.component';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { CommonModule } from '@angular/common';
import { of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, pairwise, switchMap, map, takeUntil } from 'rxjs/operators';

import { AuthorReactiveFormService } from '../services/author-reactive-form.service';
import { IFetchSimulationDetails } from 'src/app/interfaces/self-serve/responses/ifetch-simulation-details';
import { IGetWordDetailsResponse } from 'src/app/interfaces/self-serve/responses/iget-word-details-response';
import { ISimulationDetail } from 'src/app/interfaces/self-serve/isimulation-details-information';
import { DeleteModalComponent } from '../components/modals/delete-modal/delete-modal.component';
import { ActionModalComponent } from '../components/modals/simulation-assignment-action-modal/action-modal.component';
import {
  CallTypeLabelMap,
  SimulationConstants,
  SimulationMenuAction,
  SimulationMenuItem,
} from 'src/app/utils/constants/self-serve/simulation.constants';
import {
  getKnowledgeCheckTabs,
  getSimBuilderTabs,
  shouldSkipConversePromptValidation,
  SimBuilderTab,
  SimBuilderTabValues,
} from 'src/app/utils/constants/self-serve/simulation-details.constants';
import {
  EntityActionType,
  EntityType,
} from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { GuidedSimulationDetailsComponent } from './guided-simulation-details/guided-simulation-details.component';
import { PreviewSimulationDetailsModalComponent } from '../components/modals/preview-simulation-details/preview-simulation-modal.component';
import { TabsInnerWrapComponent } from '../components/tabs-inner-wrap/tabs-inner-wrap.component';
import { CapitalizePipe } from 'src/app/pipes/capitalize.pipe';
import { LocaleLabelPipe } from 'src/app/pipes/locale-punctuation.pipe';
import { SIMULATION_NAME_ALREADY_EXISTS_STATUS } from 'src/app/services/self-serve/author.service';
import { IConfirmActionPayload } from 'src/app/interfaces/self-serve/requests/iconfirm-action-payload';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AppConstants } from 'src/app/app-constants';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { getLocalStorageRoleNames, SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { hasNoMessagingThemes, hasIncompleteMessagingTheme } from 'src/app/utils/messaging-facts.util';
import { hasMissingSkillsRubric } from 'src/app/utils/skills-rubric.util';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TestSimulationLauncherService } from 'src/app/services/self-serve/test-simulation-launcher.service';
import { SelfAuthoringPersonaFormService } from '../services/self-authoring-persona-form.service';
import { EditSimulationTagsModalComponent } from '../components/modals/edit-simulation-tags-modal/edit-simulation-tags-modal.component';
import { PipesModule } from 'src/app/pipes/pipes.module';
import { KnowledgeCheckSimulationInformationComponent } from './general-details/knowledge-check-simulation-information/knowledge-check-simulation-information.component';
import { KnowledgeCheckScoringSectionComponent } from './general-details/knowledge-check-scoring-section/knowledge-check-scoring-section.component';
import { KnowledgeCheckQuestionsTabComponent } from './questions-tab/knowledge-check-questions-tab.component';
import { CallRefinementPollingService } from 'src/app/services/self-serve/call-refinement-polling.service';
import { CallRefinementStatus } from 'src/app/utils/enums/self-serve/call-refinement-status.enum';
import { ScoringRefinementService } from 'src/app/services/self-serve/scoring-refinement.service';

@Component({
  selector: 'simulation',
  templateUrl: './simulation-details.component.html',
  styleUrls: ['./simulation-details.component.scss'],
  standalone: true,
  imports: [
    RouterModule,
    FormsModule,
    QaiButtonComponent,
    QaiBreadcrumbListComponent,
    DeleteModalComponent,
    ActionModalComponent,
    QaiTabsComponent,
    QaiTabDirective,
    GeneralContentComponent,
    TestingDetailsComponent,
    ContentDetailComponent,
    ScoringDetailsComponent,
    PublishSimulationModalComponent,
    QaiKebabMenuComponent,
    PipesModule,
    CommonModule,
    GuidedSimulationDetailsComponent,
    KeyWordsDetailsComponent,
    WhatUsersSeeDetailsComponent,
    QaiTooltipComponent,
    QaiFloatingDirective,
    TranslocoModule,
    QaiSimulationCardComponent,
    PreviewSimulationDetailsModalComponent,
    TabsInnerWrapComponent,
    CapitalizePipe,
    LocaleLabelPipe,
    EditSimulationTagsModalComponent,
    KnowledgeCheckSimulationInformationComponent,
    KnowledgeCheckScoringSectionComponent,
    KnowledgeCheckQuestionsTabComponent,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class SimulationDetailsComponent {
  private readonly clipboard = inject(Clipboard);
  private readonly injector = inject(Injector);
  private readonly toastService = inject(QaiToastService);
  private readonly translocoService = inject(TranslocoService);

  simulationName: string = '';
  description: string = '';
  openPublishModal = false;
  private readonly promptRefreshStop$ = new Subject<void>();
  private readonly promptRefreshTrigger$ = new Subject<string>();
  duplicateModalVisible = false;
  networkRequestInProgress = false;
  simulationToDuplicateName: string = '';
  modalErrorStatus?: number;

  options: SimulationMenuItem[] = [];
  isEditTagsModalOpen = false;

  form!: FormGroup;
  lastSaved: string | null = null;
  callUuid: string = '';
  loading: boolean = false;
  readOnly: boolean = false;
  readyForPublish: boolean = false;
  isDeleteModalOpen = false;
  callType: string | null = null;
  showKeyWordsTab: boolean = false;
  isSelfAuthoring: boolean = false;
  isQcAdmin: boolean = false;
  visibleTabs: SimBuilderTab[] = [];
  isLaunchingTestSimulation: boolean = false;
  isExportingCallDetails: boolean = false;
  isSelfAuthoringKnowledgeCheckEnabled: boolean = false;
  isKnowledgeCheck: boolean = false;

  simulationDetails: IFetchSimulationDetails | null = null;

  isLegacyCall: boolean = false;
  openPreviewModal = false;
  previewTabs: { label: string; content: string }[] = [];
  previewModalButtonState: boolean = true;

  // Flag on: Knowledge Check uses the tabbed details page (read-only once published).
  // Flag off: the read-only guided card.
  get showKnowledgeCheckTabs(): boolean {
    return this.isKnowledgeCheck && this.isSelfAuthoringKnowledgeCheckEnabled;
  }

  get showLegacyKnowledgeCheckView(): boolean {
    return this.isKnowledgeCheck && !this.isSelfAuthoringKnowledgeCheckEnabled;
  }

  get isV2WithNoFacts(): boolean {
    const promptType = this.form?.get(`${SimulationConstants.MESSAGING_PROMPT_PATH}.promptType`)?.value;
    const factSections = this.form?.get(
      `${SimulationConstants.SCORING_SECTION}.${SimulationConstants.MESSAGING_THEME_SECTION}.${SimulationConstants.FACT_SECTIONS_SECTION}`
    ) as FormArray | null;
    return (
      promptType === SimulationConstants.MESSAGING_PROMPT_TYPE_V2 &&
      (!factSections || factSections.length === 0)
    );
  }

  get isMissingMessagingThemes(): boolean {
    return this.isSelfAuthoring && !!this.form && hasNoMessagingThemes(this.form);
  }

  get hasEmptyFacts(): boolean {
    return this.isSelfAuthoring && !!this.form && hasIncompleteMessagingTheme(this.form);
  }

  get isMissingSkillsRubric(): boolean {
    return this.isSelfAuthoring && !!this.form && hasMissingSkillsRubric(this.form);
  }

  get isSelfAuthoringPersonaSaving(): boolean {
    if (!this.isSelfAuthoring) return false;
    const personaForm = this.selfAuthoringPersonaFormService.getOrCreateForm(this.callUuid);
    const hasExistingPersona = !!personaForm.get('personaId')?.value;
    return !hasExistingPersona && this.selfAuthoringPersonaFormService.isSaving(this.callUuid)();
  }

  /** True while a refinement is scheduled (ScoringRefinementService debounce) or still running (polling). */
  get isRefinementInProgress(): boolean {
    return (
      this.callRefinementPollingService.isRefining(this.callUuid) ||
      this.scoringRefinementService.isPending(this.callUuid)
    );
  }

  private get knowledgeCheckQuestions(): FormArray | null {
    return this.form?.get(SimulationConstants.QUESTIONS_SECTION) as FormArray | null;
  }

  /** A Knowledge Check needs at least one question to publish. Kept separate from
   * hasIncompleteKnowledgeCheckQuestions so its "title and script" tooltip doesn't show when there are no questions. */
  get hasNoKnowledgeCheckQuestions(): boolean {
    return !this.knowledgeCheckQuestions?.length;
  }

  /** Every existing question needs a title and a script (hasNoKnowledgeCheckQuestions covers zero questions). */
  get hasIncompleteKnowledgeCheckQuestions(): boolean {
    return (this.knowledgeCheckQuestions?.controls ?? []).some(
      (question) =>
        !question.get('title')?.value?.toString().trim() || !question.get('script')?.value?.toString().trim()
    );
  }

  /** Every question needs at least one answer point, e.g. after moving its last one to another question. */
  get hasKnowledgeCheckQuestionWithoutAnswerPoints(): boolean {
    return (this.knowledgeCheckQuestions?.controls ?? []).some(
      (question) => !(question.get('answerPoints') as FormArray | null)?.length
    );
  }

  /** Every answer point needs a verb and a correct text (displayText) to publish. */
  get hasIncompleteKnowledgeCheckAnswerPoints(): boolean {
    return (this.knowledgeCheckQuestions?.controls ?? []).some((question) => {
      const answerPoints = question.get('answerPoints') as FormArray | null;
      return (answerPoints?.controls ?? []).some(
        (ap) =>
          !ap.get('verb')?.value?.toString().trim() ||
          !ap.get('displayText')?.value?.toString().trim(),
      );
    });
  }

  get hasSavedKnowledgeCheckPersona(): boolean {
    return !!this.selfAuthoringPersonaFormService.getOrCreateForm(this.callUuid).get('personaId')?.value;
  }

  get isReadyForPublish(): boolean {
    if (this.showKnowledgeCheckTabs) {
      return (
        !!this.form?.valid &&
        !this.hasNoKnowledgeCheckQuestions &&
        !this.hasIncompleteKnowledgeCheckQuestions &&
        !this.hasKnowledgeCheckQuestionWithoutAnswerPoints &&
        !this.hasIncompleteKnowledgeCheckAnswerPoints &&
        this.hasSavedKnowledgeCheckPersona &&
        !this.isSelfAuthoringPersonaSaving
      );
    }
    return (
      !!this.form?.valid &&
      !this.isV2WithNoFacts &&
      !this.isMissingMessagingThemes &&
      !this.hasEmptyFacts &&
      !this.isMissingSkillsRubric &&
      !this.isSelfAuthoringPersonaSaving
    );
  }

  get canTestSimulation(): boolean {
    if (!this.callUuid || this.isLaunchingTestSimulation || this.showKnowledgeCheckTabs) {
      return false;
    }
    const replicaId = this.selfAuthoringPersonaFormService.getOrCreateForm(this.callUuid).get('replicaId')?.value;
    return !!replicaId;
  }

  get canExportCallDetails(): boolean {
    if (!this.callUuid || this.isExportingCallDetails || this.showKnowledgeCheckTabs) {
      return false;
    }
    const replicaId = this.form?.get(`${SimulationConstants.CALL_PERSONA_PATH}.replicaId`)?.value;
    return !!replicaId && this.isReadyForPublish;
  }

  constructor(
    private authorService: AuthorService,
    private route: ActivatedRoute,
    private authorReactiveFormService: AuthorReactiveFormService,
    private router: Router,
    private cdRef: ChangeDetectorRef,
    private featureFlagsService: FeatureFlagsService,
    private amplitudeService: AmplitudeService,
    private testSimulationLauncherService: TestSimulationLauncherService,
    private destroyRef: DestroyRef,
    private selfAuthoringPersonaFormService: SelfAuthoringPersonaFormService,
    private callRefinementPollingService: CallRefinementPollingService,
    private scoringRefinementService: ScoringRefinementService
  ) {}

  EntityActionType = EntityActionType;
  EntityType = EntityType;
  protected readonly SimBuilderTabValues = SimBuilderTabValues;

  async ngOnInit(): Promise<void> {
    this.promptRefreshTrigger$
      .pipe(
        switchMap((callUuid) => {
          const moduleUuid = this.route.snapshot.queryParamMap.get('moduleUuid') || '';
          return this.authorService.getSimulationDetails(callUuid, moduleUuid).pipe(
            map((details) => ({ callUuid, details })),
            catchError(() => of(null)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (!result) return;
        // If the admin saved a manual override (even in-flight while this response was
        // in transit), don't overwrite it with the refinement result.
        if (this.simulationDetails?.conversePromptManualOverride) return;
        const converse = result.details?.callPrompts?.prompts?.find(
          (p: { promptType?: string }) => p.promptType?.toUpperCase() === SimulationConstants.CONVERSE_PROMPT_TYPE
        );
        if (converse) {
          this.authorReactiveFormService.patchFormSilently(
            result.callUuid,
            SimulationConstants.CONVERSE_PROMPT_PATH,
            converse,
          );
          // patchFormSilently uses onlySelf:true, so intermediate groups (e.g. "content")
          // have stale cached status. Call updateValueAndValidity on the patched section
          // group itself — it bubbles up through the tree without onlySelf, so every
          // ancestor (including root) recalculates correctly and the publish button reflects
          // the new prompt value.
          this.form?.get(SimulationConstants.CONVERSE_PROMPT_PATH)?.updateValueAndValidity();
        }
      });
    this.showKeyWordsTab = await this.featureFlagsService.isFlagEnabled(AppConstants.KEY_WORDS);
    console.log('Key_Words feature flag:', this.showKeyWordsTab);
    const isSelfAuthoringRoleplayEnabled =
      await this.featureFlagsService.isFlagEnabled(
        AppConstants.SELF_AUTHORING_ROLEPLAY
      );

    this.isSelfAuthoringKnowledgeCheckEnabled = await this.featureFlagsService.isFlagEnabled(
      AppConstants.SELF_AUTHORING_KNOWLEDGE_CHECK
    );

    const hasQcAdminRole = getLocalStorageRoleNames().includes('QCAdmin');
    this.isQcAdmin = isSelfAuthoringRoleplayEnabled && hasQcAdminRole;
    console.log('isQcAdmin:', this.isQcAdmin);

    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const isAuthor = accountUuid
      ? SelfServeLocalStorageUtil.getSelectedAccountRoles(accountUuid)
          .hasAuthor
      : false;

    this.isSelfAuthoring = isSelfAuthoringRoleplayEnabled && isAuthor;
    this.visibleTabs = getSimBuilderTabs({
      roles: getLocalStorageRoleNames(),
      isSelfAuthoringRoleplayEnabled,
      isAuthor,
      showKeyWords: this.showKeyWordsTab,
    });

    this.route.paramMap?.subscribe((params) => {
      const navigation = this.router.getCurrentNavigation();
      const routeState = navigation?.extras?.state ?? history.state;

      this.callType = routeState?.callType || null;
      this.isKnowledgeCheck = this.callType === CallTypeLabelMap.guided;
      const moduleUuid = this.route.snapshot.queryParamMap.get('moduleUuid') || '';

      const uuid = params.get('uuid');
      if (!uuid) return;

      this.callUuid = uuid;
      this.loading = true;

      this.authorService
        .getSimulationDetails(this.callUuid, moduleUuid)
        .pipe(
          switchMap((simulationDetails) => {
            if (!simulationDetails) {
              console.error('Failed to fetch simulation details');
              this.loading = false;
              return of({ simulationDetails: null, keywords: undefined });
            }

            const personaUuid = simulationDetails.callPersona?.uuid || '';

            // Fetch keywords with personaUuid
            return this.authorService
              .getWordDetails(this.callUuid, personaUuid)
              .pipe(
                catchError((error) => {
                  console.warn(
                    'Failed to fetch keywords, continuing with empty keywords:',
                    error
                  );
                  return of(undefined);
                }),
                map((keywords) => ({ simulationDetails, keywords }))
              );
          })
        )
        .subscribe({
          next: ({ simulationDetails, keywords }) => {
            if (!simulationDetails) {
              return;
            }

          this.simulationDetails = simulationDetails;
          // Router state is missing after create or on a deep link, so fall back to the API call type.
          this.isKnowledgeCheck =
              simulationDetails.callType?.toUpperCase() === AppConstants.CALL_TYPE_GUIDED;
          if (this.showKnowledgeCheckTabs) {
            this.visibleTabs = getKnowledgeCheckTabs();
          }

          if (this.isSelfAuthoring || this.showKnowledgeCheckTabs) {
            this.selfAuthoringPersonaFormService.initializeForm(
              this.callUuid,
              {
                uuid: simulationDetails.callPersona?.uuid,
                personaId: simulationDetails.callPersona?.personaId,
                personaName: simulationDetails.callPersona?.personaName,
                replicaId: simulationDetails.callPersona?.replicaId,
                welcomeMessage: simulationDetails.callPersona?.welcomeMessage,
                resumeMessage: simulationDetails.callPersona?.resumeMessage,
              },
              { mode: this.showKnowledgeCheckTabs ? 'echo' : 'custom' }
            );
          }
          this.isLegacyCall =
            this.isSelfAuthoring &&
            simulationDetails.hasSimulationGeneration === false &&
            !this.isKnowledgeCheck;
          this.previewModalButtonState = !simulationDetails.callDetailsList?.callDetails?.some(
            (tab: ISimulationDetail) => tab.tabText?.trim()
          );

          this.authorReactiveFormService.setSelfAuthoring(this.isSelfAuthoring);

          const skipConversePromptValidation = shouldSkipConversePromptValidation({
            isSelfAuthoringRoleplayEnabled,
            hasSimulationGeneration: simulationDetails.hasSimulationGeneration,
            isAuthor,
            hasQcAdminRole,
            hasConversePrompt: simulationDetails.hasConversePrompt,
          });

          // Initialize form with both simulation details and keywords
          this.form = this.authorReactiveFormService.initializeSimulationForm(
            this.callUuid,
            simulationDetails,
            keywords,  // Pass keywords data here
            { skipConversePromptValidation, isKnowledgeCheck: this.showKnowledgeCheckTabs }
          );

          if (this.isQcAdmin) {
            this.setupPromptRefreshOnRefinement(uuid);
          }

          this.simulationName =
            this.form.get('general.simulationName')?.value || '';

          this.form
            .get('general.simulationName')
            ?.valueChanges?.subscribe((value) => {
              this.simulationName = value;
            });

          this.readOnly = this.authorReactiveFormService.isReadOnly(
            this.callUuid
          );

          this.form?.statusChanges?.subscribe(() => {
            this.readOnly = this.authorReactiveFormService.isReadOnly(
              this.callUuid
            );
            this.readyForPublish = this.form.valid;
          });

          // SAFE: status enum value
          const status = simulationDetails.status?.toLowerCase();

          const t = (key: string) => this.translocoService.translate(`simulationBuilder.${key}`);
          const copyUuidOption: SimulationMenuItem = {
            type: 'click',
            action: SimulationMenuAction.CopyUuid,
            text: t('MENU_COPY_UUID'),
            description: this.callUuid,
          };

          if (this.showLegacyKnowledgeCheckView || this.showKnowledgeCheckTabs || this.isLegacyCall) {
            this.options = [copyUuidOption];
          } else {
            this.options = [
              { type: 'click', action: SimulationMenuAction.Duplicate, text: t('ACTION_DUPLICATE') },
              ...(status !== 'published'
                ? [{ type: 'click', action: SimulationMenuAction.Delete, text: t('DELETE') } as SimulationMenuItem]
                : []),
              ...(simulationDetails.hasSimulationGeneration && status !== 'published'
                ? [
                    {
                      type: 'click',
                      action: SimulationMenuAction.EditTags,
                      text: t('MENU_EDIT_SIMULATION_TAGS'),
                    } as SimulationMenuItem,
                  ]
                : []),
              copyUuidOption,
            ];
          }

          this.loading = false;
        },
        error: (error) => {
          console.error('Error fetching simulation data:', error);
          this.loading = false;
        }
      });

      this.authorReactiveFormService
        .getUpdatedAt$(uuid)
        ?.subscribe((timestamp) => {
          this.lastSaved = timestamp;
        });
    });
  }

  ngOnDestroy() {
    this.authorReactiveFormService.deleteSimulation(this.callUuid);
  }

  onTabSelected(event: { id: string; label?: string }): void {
    console.log('Tab selected:', event.label ?? event.id)
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.SELF_AUTHORING_SIMBUILDER_TAB_VIEWED, {
      tab_name: event.label ?? event.id,
    });
  }

  publishSimulation(): void {
    if (this.isRefinementInProgress) return;
    this.openPublishModal = true;
  }

  handleTestSimulationClick(): void {
    const moduleUuid = this.form?.get(SimulationConstants.MODULE_UUID_PATH)?.value || '';
    const currentUserUuid = SelfServeLocalStorageUtil.getCurrentUser().userUuid;

    if (!this.canTestSimulation || !moduleUuid || !currentUserUuid) {
      return;
    }

    this.isLaunchingTestSimulation = true;

    this.testSimulationLauncherService
      .launchTestSimulation(this.callUuid, moduleUuid, currentUserUuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLaunchingTestSimulation = false;
        },
        error: (err) => {
          this.isLaunchingTestSimulation = false;
          console.error('Test simulation launch failed', err);
        },
      });
    }

  handleExportCallDetailsClick(): void {
    if (!this.canExportCallDetails) {
      return;
    }

    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.SELF_AUTHORING_CALL_DETAILS_EXPORT_CLICKED, {
      call_uuid: this.callUuid,
    });

    this.isExportingCallDetails = true;

    this.authorService
      .exportCallDetails(this.callUuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          this.isExportingCallDetails = false;
          this.downloadCallDetailsExport(blob);
        },
        error: (err) => {
          this.isExportingCallDetails = false;
          console.error('Export call details failed', err);
        },
      });
  }

  private downloadCallDetailsExport(blob: Blob): void {
    const docxBlob = new Blob([blob], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    const url = window.URL.createObjectURL(docxBlob);
    const safeName = (this.simulationName || 'simulation').replace(/[\\/:*?"<>|]+/g, '-');

    const link = document.createElement('a');
    link.href = url;
    link.download = `${safeName} - Summary.docx`;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  callDetailsClick(event: any) {
    const tabs: ISimulationDetail[] = this.simulationDetails?.callDetailsList?.callDetails ?? [];
    this.previewTabs = [...tabs]
      .sort((a, b) => a.tabSequence - b.tabSequence)
      .map((tab) => ({ label: tab.tabTitle, content: tab.tabText }));
    this.openPreviewModal = true;
  }

  handlePreviewModalClose() {
    this.openPreviewModal = false;
  }

  handleConversePromptOverridden(): void {
    if (this.simulationDetails) {
      this.simulationDetails = { ...this.simulationDetails, conversePromptManualOverride: true };
    }
  }

  handlePublishModalConfirm() {
    this.openPublishModal = false;
  }

  handlePublishModalClose() {
    this.openPublishModal = false;
  }

  onKebabMenuAction(event: { item: KebabMenuItem }) {
    switch ((event.item as SimulationMenuItem).action) {
      case SimulationMenuAction.Duplicate:
        this.simulationToDuplicateName = this.simulationName;
        this.duplicateModalVisible = true;
        break;
      case SimulationMenuAction.Delete:
        this.openDeleteModal();
        break;
      case SimulationMenuAction.EditTags:
        this.isEditTagsModalOpen = true;
        break;
      case SimulationMenuAction.CopyUuid:
        this.copyCallUuid();
        break;
    }
  }

  private copyCallUuid(): void {
    if (!this.clipboard.copy(this.callUuid)) return;

    this.toastService.addMessage({
      id: `toast-copy-uuid-${Date.now()}`,
      kind: 'success',
      content: this.translocoService.translate('simulationBuilder.UUID_COPIED_TOAST'),
      duration: 3000,
    });
  }

  // Refetches the details but patches in only the tags, so a tags save never re-initialises the
  // form (which would drop unsaved edits) or re-fetches keywords.
  refreshSimulationTags(): void {
    const moduleUuid = this.route.snapshot.queryParamMap.get('moduleUuid') || '';

    this.authorService.getSimulationDetails(this.callUuid, moduleUuid).subscribe({
      next: (simulationDetails: IFetchSimulationDetails) => {
        if (this.simulationDetails && simulationDetails) {
          this.simulationDetails = { ...this.simulationDetails, tags: simulationDetails.tags };
        }
      },
      error: (error) => {
        console.error('Error refreshing simulation tags:', error);
      },
    });
  }

  onSimulationDuplicated(data: IConfirmActionPayload) {
    this.networkRequestInProgress = true;
    this.modalErrorStatus = undefined;

    const payload = {
      callUuid: this.callUuid,
      callTitle: data.name,
      moduleUuid: data.moduleUuid || this.form?.get(SimulationConstants.MODULE_UUID_PATH)?.value || '',
    };

    this.authorService.duplicateSimulationAPI(payload).subscribe({
      next: (newSimulation) => {
        this.closeDuplicateModal();
        const moduleUuid = payload.moduleUuid;
        this.router.navigate(['/self-serve/simulations', newSimulation.uuid], {
          queryParams: { moduleUuid, fromSimulationDetails: 'true' },
        });
      },
      error: (err) => {
        console.error('Error duplicating simulation:', err);
        this.networkRequestInProgress = false;

        if (err?.status === SIMULATION_NAME_ALREADY_EXISTS_STATUS) {
          this.modalErrorStatus = err.status;
        }
      },
      complete: () => {
        this.networkRequestInProgress = false;
      },
    });
  }

  closeDuplicateModal() {
    this.duplicateModalVisible = false;
    this.simulationToDuplicateName = '';
    this.modalErrorStatus = undefined;
    this.cdRef.detectChanges();
  }

  openDeleteModal() {
    this.isDeleteModalOpen = true;
  }

  confirmDelete() {
    this.networkRequestInProgress = true;
    const moduleUuid = this.form?.get(SimulationConstants.MODULE_UUID_PATH)?.value || '';
    this.authorService.deleteSimulationAPI(this.callUuid, moduleUuid).subscribe({
      next: () => {
        this.isDeleteModalOpen = false;
        this.router.navigate(['/self-serve'], {
          queryParams: { fromSimulationDetails: 'true' },
        });
        this.networkRequestInProgress = false;
      },
      error: (err) => {
        console.error('Error deleting simulation:', err);
        this.networkRequestInProgress = false;
      },
    });
  }

  private setupPromptRefreshOnRefinement(callUuid: string): void {
    this.promptRefreshStop$.next(); // cancel any previous subscription (component reuse)
    // React to the signal directly so we catch Updated the instant it arrives —
    // before generated-simulation-information.ngOnDestroy() calls dismiss() and
    // clears the record (which would reset the status to Idle within the 3s window
    // that a poll-based approach would have missed).
    toObservable(
      computed(() => this.callRefinementPollingService.getStatus(callUuid)),
      { injector: this.injector },
    )
      .pipe(
        distinctUntilChanged(),
        pairwise(),
        filter(([prev, curr]) =>
          prev === CallRefinementStatus.Thinking && curr === CallRefinementStatus.Updated,
        ),
        takeUntil(this.promptRefreshStop$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.refreshConversePrompt(callUuid));
  }

  private refreshConversePrompt(callUuid: string): void {
    this.promptRefreshTrigger$.next(callUuid);
  }
}
